import { type FC, type ReactNode } from "react";

import { clsx } from "clsx";

import { type IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faBooks } from "@fortawesome/pro-duotone-svg-icons/faBooks";
import { faMagnifyingGlass } from "@fortawesome/pro-duotone-svg-icons/faMagnifyingGlass";
import { faPenLine } from "@fortawesome/pro-duotone-svg-icons/faPenLine";
import { faSparkles } from "@fortawesome/pro-duotone-svg-icons/faSparkles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { THINKING_LABEL } from "./chat_bubble";

// "Read X of Y letters in this chat": how much of the case the assistant has
// read in the chat you're in, with a bar while it's partway. Once it has read
// them all, a small "Read all Y letters". Violet, because it's the assistant's.
export const ReadCoverage: FC<{
  read: number;
  total: number;
  noun: "letters" | "pages";
  trailing?: ReactNode;
}> = ({ read, total, noun, trailing }) =>
  total > 0 && read >= total ? (
    <div className="flex shrink-0 items-center gap-2 px-1 pb-2 text-[11.5px] whitespace-nowrap text-slate-500">
      <span className="size-[5px] shrink-0 rounded-full bg-violet-500" />
      <span className="flex-1">
        Read all {total} {noun}
      </span>
      {trailing}
    </div>
  ) : (
    <div className="glass flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 shadow-[0_0_0_1px_rgba(18,53,91,.06)]">
      <span className="size-[7px] shrink-0 rounded-full bg-violet-700 shadow-[0_0_0_3px_rgba(109,40,217,.14)]" />
      <span className="text-muted text-[11.5px] whitespace-nowrap">
        <b className="text-ink">
          Read {read} of {total}
        </b>{" "}
        {noun} in this chat
      </span>
      <span className="ml-1 h-[5px] flex-1 overflow-hidden rounded-full bg-slate-100">
        <span
          className="block h-full rounded-full bg-violet-400"
          style={{
            width: `${String(total > 0 ? (read * 100) / total : 0)}%`,
          }}
        />
      </span>
      {trailing}
    </div>
  );

// "Checked references 5 times": how often this chat looked in our reference
// files, beside the coverage. Nothing until it has.
export const ReferencesCount: FC<{ count: number }> = ({ count }) =>
  count > 0 && (
    <span className="flex shrink-0 items-center gap-1.5 border-l border-violet-100 pl-2 text-[11.5px] whitespace-nowrap text-slate-700">
      <FontAwesomeIcon icon={faBooks} className="text-violet-600" />
      Checked references {count === 1 ? "once" : `${String(count)} times`}
    </span>
  );

// The step the assistant is on, over the pane: "Checking references",
// "Looking at the case's files · 4 files", or a search.
export const AssistantStep: FC<{
  icon: IconDefinition;
  label: ReactNode;
  detail?: string;
  pulse?: boolean;
  hint?: string;
}> = ({ icon, label, detail, pulse = false, hint }) => (
  <div
    title={hint}
    className="glass flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-[11.5px] text-slate-700 shadow-[0_0_0_1px_rgba(18,53,91,.06)]"
  >
    {hint && <span className="sr-only">{hint}</span>}
    <FontAwesomeIcon
      icon={icon}
      className={clsx(
        "text-xs text-violet-600",
        pulse && "animate-pulse motion-reduce:animate-none",
      )}
    />
    <b
      aria-hidden={hint ? true : undefined}
      className="min-w-0 truncate text-slate-900"
    >
      {label}
    </b>
    {detail && (
      <span
        aria-hidden={hint ? true : undefined}
        className="shrink-0 whitespace-nowrap text-slate-500"
      >
        · {detail}
      </span>
    )}
  </div>
);

// "Thinking... · 12s": the assistant between steps, the icon pulsing so a long
// think still reads as work. Seconds show once it's been a while.
export const ThinkingStep: FC<{ seconds: number }> = ({ seconds }) => (
  <AssistantStep
    icon={faSparkles}
    label={THINKING_LABEL}
    detail={seconds >= 5 ? `${String(seconds)}s` : undefined}
    pulse
  />
);

// "Searched “knee MRI” · 10 found": the search the assistant just ran, in
// place of the coverage while it looks at the results.
export const SearchedFor: FC<{ query: string; found: number }> = ({
  query,
  found,
}) => (
  <AssistantStep
    icon={faMagnifyingGlass}
    label={
      <span title={query}>
        <span className="font-normal text-slate-700">Searched</span> “{query}”
      </span>
    }
    detail={found > 0 ? `${String(found)} found` : undefined}
  />
);

// "Writing Discussion": the report section the assistant is typing into the
// Word report.
export const WritingSection: FC<{ name: string }> = ({ name }) => (
  <AssistantStep
    icon={faPenLine}
    label={
      <span title={name}>
        <span className="font-normal text-slate-700">Writing</span> {name}
      </span>
    }
  />
);

// Where reading stopped, under the last letter read in this chat.
export const NotReadDivider: FC = () => (
  <div className="my-0.5 flex items-center gap-2 text-[10.5px] font-semibold tracking-[0.05em] text-slate-400">
    <span className="h-px flex-1 bg-slate-200" />
    NOT READ IN THIS CHAT
    <span className="h-px flex-1 bg-slate-200" />
  </div>
);
